import React, { useEffect } from 'react';
import { Author, Article, Category } from '../../../models/article';
import PopularBlock, { PopularBlockData } from './Blocks/PopularBlock';
import ContentBlock, { ContentBlockData } from './Blocks/ContentBlock';
import { Meetup, TitleBlock } from '../../../models/collection';
import { Brand } from '../store/selectors';
import MeetupBlock, { MeetupBlockData } from './Blocks/MeetupBlock';
import HolidayBlock, { HolidayBlockData } from './Blocks/HolidayBlock';
import StoriesBlock, { ArticleBlockData } from './Blocks/ArticleBlock';
import CardBlock, { CardBlockData } from './Blocks/CardBlock';
import CTABlock, { CTABlockData } from './Blocks/CTABlock';
import HeaderBlock, { HeaderBlockData } from './Blocks/HeaderBlock';
import AlgoliaBlock, { AlgoliaBlockData } from './Blocks/AlgoliaBlock';
import ExternalSearchBlock, {
  ExternalSearchBlockData,
} from '../../TravelCollection/ExternalSearchBlock';
import CarouselBlock, { CarouselBlockData } from './Blocks/CarouselBlock';
import AdBlock, { AdBlockData } from './Blocks/AdBlock';
import AgentSelectBlock, {
  AgentSelectBlockData,
} from './Blocks/AgentSelectBlock';
import OnboardingBlock, { OnboardingBlockData } from './Blocks/OnboardingBlock';
import HubBlock, { HubBlockData } from './Blocks/HubBlock';
import { Ads } from '../../../components/presentational/Ad';
import PanelBlock, { PanelBlockData } from './Blocks/PanelBlock';
import ExplainerBlock, { ExplainerBlockData } from './Blocks/ExplainerBlock';
import BreakdownBlock, { BreakdownBlockData } from './Blocks/BreakdownBlock';
import SponsorBlock, { SponsorBlockData } from './Blocks/SponsorBlock';
// Should probably move the workflow data at some point
import MembershipBlock, { MembershipBlockData } from './Blocks/MembershipBlock';
import FAQBlock, { FAQBlockData } from './Blocks/FAQBlock';
import SummaryBlock, { SummaryBlockData } from './Blocks/SummaryBlock';
import IconContainerBlock, {
  IconContainerBlockData,
} from './Blocks/IconContainerBlock';
import CardGridBlock, { CardGridBlockData } from './Blocks/CardGridBlock';
import MemberBenefitBlock, {
  MemberDealBlockData,
} from './Blocks/MemberDealBlock';
import SpacerBlock, { SpacerBlockData } from './Blocks/SpacerBlock/SpacerBlock';
import TravelHolidayBlock, {
  TravelHolidayBlockData,
} from './Blocks/TravelHolidayBlock';
import { loadMoreGroupHolidays } from '../../TravelCollection/api';
import HeroTilesBlock, {
  HeroTilesBlockData,
} from './Blocks/HeroTilesBlock/HeroTilesBlock';
import CompetitionVoteBlock, { CompetitionVoteBlockData } from './Blocks/CompetitionVoteBlock/CompetitionVoteBlock';
import InsuranceBlock, { InsuranceBlockData } from './Blocks/InsuranceBlock/InsuranceBlock';
import MenuTileBlock, { MenuTileBlockData } from './Blocks/MenuTileBlock/MenuTileBlock';
import PillarArticlesBlock, { PillarArticlesBlockData } from './Blocks/PillarArticlesBlock/PillarArticlesBlock';
import { MenuLink } from '../../../models/menu';
import { CategoryArticlesBlock, CategoryArticlesBlockData } from './Blocks/CategoryArticlesBlock/CategoryArticlesBlock';
import { DeveloperBlock, DeveloperBlockData } from './Blocks/DeveloperBlock';
import CampaignBannerBlock, {
  CampaignBannerBlockData,
} from './Blocks/CampaignBannerBlock';

export type Block =
  | ArticleBlockData
  | MeetupBlockData
  | ContentBlockData
  | HolidayBlockData
  | PopularBlockData
  | CardBlockData
  | CTABlockData
  | AdBlockData
  | AgentSelectBlockData
  | OnboardingBlockData
  | HubBlockData
  | PanelBlockData
  | ExplainerBlockData
  | BreakdownBlockData
  | SponsorBlockData
  | MembershipBlockData
  | FAQBlockData
  | SummaryBlockData
  | IconContainerBlockData
  | HeaderBlockData
  | TitleBlock
  | AlgoliaBlockData
  | ExternalSearchBlockData
  | CarouselBlockData
  | CardGridBlockData
  | MemberDealBlockData
  | SpacerBlockData
  | TravelHolidayBlockData
  | HeroTilesBlockData
  | CompetitionVoteBlockData
  | InsuranceBlockData
  | MenuTileBlockData
  | PillarArticlesBlockData
	| CategoryArticlesBlockData
	| DeveloperBlockData
  | CampaignBannerBlockData
  | { type: 'acf_content_block' };

interface CollectionBlockProps {
  block: Block;
  ads: Ads;
  blockIndex: number;
  categories: Category[];
  links?: { id: number, label: string, url: string }[];
  categoryDescendents?: { id: number, label: string, url: string }[];
  backfillCategory?: Category;
  author: Author | undefined;
  brand: Brand;
  popular: Article[];
  hideAds: boolean;
  meetups: Meetup[];
  loadMore: () => any;
  moreArticlesLoading: boolean | undefined;
  articleIds;
  collectionConfig?: { [key: string]: any; };
}

const CollectionBlock = ({
  block,
  ads,
  blockIndex,
  categories,
  links = [],
  categoryDescendents = [],
  backfillCategory,
  author,
  hideAds,
  loadMore,
  moreArticlesLoading,
  meetups,
  brand,
  popular,
  articleIds,
  collectionConfig = {},
}: CollectionBlockProps) => {

  // useEffect(() => {
  //   console.log('CollectionBlock', { hideAds, ads, categories, links, categoryDescendents, });
  // }, []);
  
  switch (block.type) {
		case 'category_articles_block':
			return (
				<CategoryArticlesBlock block={block} key={blockIndex} />
			);
    case 'developer_block':
      return (
        <DeveloperBlock block={block} key={blockIndex} />
      );
    case 'article_block':
      return (
        <StoriesBlock
          block={block}
          ads={ads}
          key={blockIndex}
          blockIndex={blockIndex}
          categories={categories || []}
          links={links || []}
          categoryDescendents={categoryDescendents || []}
          backfillCategory={backfillCategory}
          author={author}
          hideAds={hideAds}
          loadMore={loadMore}
          articleIds={articleIds}
          lazy={blockIndex !== 0}
          moreArticlesLoading={moreArticlesLoading}
          backfillCount={block.backfillCount}
          preferTrackingBackfill={block.preferTrackingBackfill}
          collectionConfig={collectionConfig}
        />
      );
    case 'meetup_block':
      return (
        meetups && (
          <MeetupBlock meetups={meetups} key={blockIndex} brand="sas" />
        )
      );
    case 'holiday_block':
      return <HolidayBlock block={block} key={blockIndex} />;
    case 'content_block':
    case 'acf_content_block':
      return <ContentBlock block={block} brand={brand} key={blockIndex} />;
    case 'header_block':
      return <HeaderBlock block={block} key={blockIndex} />;
    case 'title_block':
      return <></>;
    case 'popular_block':
      return <PopularBlock block={block} popular={popular} key={blockIndex} />;
    case 'card_block':
      return (
        <CardBlock lazy={blockIndex !== 0} block={block} key={blockIndex} />
      );
    case 'cta_block':
      return <CTABlock block={block} key={blockIndex} />;
    case 'faq_block':
      return <FAQBlock block={block} key={blockIndex} />;
    case 'summary_block':
      return <SummaryBlock block={block} key={blockIndex} />;
    case 'icon_container_block':
      return <IconContainerBlock block={block} key={blockIndex} />;
    case 'ad_block':
      return (
        <AdBlock
          block={block}
          ads={ads}
          key={blockIndex}
          blockIndex={blockIndex}
        />
      );
    case 'agent_select_block':
      return <AgentSelectBlock block={block} ads={ads} key={blockIndex} />;
    case 'onboarding_block':
      return <OnboardingBlock block={block} key={blockIndex} />;
    case 'panel_block':
      return (
        <PanelBlock block={block} key={blockIndex} blockIndex={blockIndex} />
      );
    case 'explainer_block':
      return <ExplainerBlock block={block} key={blockIndex} />;
    case 'breakdown_block':
      return <BreakdownBlock block={block} key={blockIndex} />;
    case 'sponsor_block':
      return <SponsorBlock block={block} key={blockIndex} />;
    case 'membership_block':
      return (
        <MembershipBlock
          block={block}
          key={blockIndex}
          blockIndex={blockIndex}
        />
      );
    case 'hub_block':
      return (
        <HubBlock
          block={block}
          key={blockIndex}
          loadMore={loadMore}
          articleIds={articleIds}
          blockIndex={blockIndex}
          moreArticlesLoading={moreArticlesLoading}
        />
      );
    case 'indexed_search_block':
      return <AlgoliaBlock block={block} key={blockIndex} />;
    case 'external_cruise_search_block':
      return <ExternalSearchBlock block={block} key={blockIndex} />;
    case 'carousel_block':
      return <CarouselBlock block={block} key={blockIndex} />;
    case 'card_grid_block':
      return <CardGridBlock block={block} key={blockIndex} />;
    case 'member_deal_block':
      return <MemberBenefitBlock block={block} key={blockIndex} />;
    case 'spacer_block':
      return <SpacerBlock block={block} key={blockIndex} />;
    case 'travel_holiday_block':
      return (
        <TravelHolidayBlock
          block={block}
          key={blockIndex}
          collectionConfig={{ ads, loadMoreGroupHolidays }}
        />
      );
    case 'hero_tiles_block':
      return <HeroTilesBlock block={block} key={blockIndex} />;
    case 'competition_vote_block':
      return <CompetitionVoteBlock block={block} key={blockIndex} />;
    case 'open_insurance_block':
      return <InsuranceBlock block={block} key={blockIndex} />;
    case 'menu_tile_block':
      return <MenuTileBlock block={block} key={blockIndex} />;
    case 'pillar_articles_block':
      return <PillarArticlesBlock block={block} key={blockIndex} />;
    case 'campaign_banner_block':
      return <CampaignBannerBlock block={block} key={blockIndex} />;

    default:
      return <div />;
  }
};

export default CollectionBlock;
